import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex cursor-default items-center gap-1 border font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
  {
    variants: {
      size: {
        sm: "h-4 px-1.5 text-xxs leading-3",
        md: "h-5 px-2 text-xs leading-4",
        lg: "h-6 px-2.5 text-sm leading-5",
      },
      variant: {
        default: "",
        primary: "",
        secondary: "",
        neutral: "",
        info: "",
        success: "",
        warning: "",
        destructive: "",
      },
      type: {
        fill: "border-transparent",
        outline: "bg-transparent",
        ghost: "border-transparent bg-transparent",
      },
      radius: {
        circle: "rounded-full",
        square: "rounded-lg",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        type: "fill",
        className: "bg-muted text-foreground hover:bg-muted/80",
      },
      {
        variant: "primary",
        type: "fill",
        className: "bg-primary text-primary-foreground hover:bg-primary/80",
      },
      {
        variant: "secondary",
        type: "fill",
        className:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      },
      {
        variant: "neutral",
        type: "fill",
        className: "bg-neutral text-neutral-foreground hover:bg-neutral/80",
      },
      {
        variant: "info",
        type: "fill",
        className: "bg-info text-info-foreground hover:bg-info/80",
      },
      {
        variant: "success",
        type: "fill",
        className: "bg-success text-success-foreground hover:bg-success/80",
      },
      {
        variant: "warning",
        type: "fill",
        className: "bg-warning text-warning-foreground hover:bg-warning/80",
      },
      {
        variant: "destructive",
        type: "fill",
        className:
          "bg-destructive text-destructive-foreground hover:bg-destructive/80",
      },
      {
        variant: "default",
        type: "outline",
        className: "border-border text-foreground",
      },
      {
        variant: "primary",
        type: "outline",
        className: "border-primary text-primary",
      },
      {
        variant: "secondary",
        type: "outline",
        className: "border-secondary text-secondary",
      },
      {
        variant: "neutral",
        type: "outline",
        className: "border-neutral text-neutral",
      },
      {
        variant: "info",
        type: "outline",
        className: "border-info text-info",
      },
      {
        variant: "success",
        type: "outline",
        className: "border-success text-success",
      },
      {
        variant: "warning",
        type: "outline",
        className: "border-warning text-warning",
      },
      {
        variant: "destructive",
        type: "outline",
        className: "border-destructive text-destructive",
      },
      {
        variant: "default",
        type: "ghost",
        className: "text-foreground hover:bg-muted/50",
      },
      {
        variant: "primary",
        type: "ghost",
        className: "text-primary hover:bg-primary/10",
      },
      {
        variant: "secondary",
        type: "ghost",
        className: "text-secondary hover:bg-secondary/10",
      },
      {
        variant: "neutral",
        type: "ghost",
        className: "text-neutral hover:bg-neutral/10",
      },
      {
        variant: "info",
        type: "ghost",
        className: "text-info hover:bg-info/10",
      },
      {
        variant: "success",
        type: "ghost",
        className: "text-success hover:bg-success/10",
      },
      {
        variant: "warning",
        type: "ghost",
        className: "text-warning hover:bg-warning/10",
      },
      {
        variant: "destructive",
        type: "ghost",
        className: "text-destructive hover:bg-destructive/10",
      },
    ],
    defaultVariants: {
      size: "md",
      variant: "default",
      type: "fill",
      radius: "circle",
    },
  }
)

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({
  className,
  size,
  variant,
  type,
  radius,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(badgeVariants({ size, variant, type, radius }), className)}
      {...props}
    />
  )
}

export { Badge }
